import { requestIdleCallback } from "@theme/utilities";
/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: "open" });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

(() => {
  function setScrollbarWidth() {
    requestIdleCallback(() => {
      // Create temporary div to measure scrollbar width
      const outer = document.createElement("div");
      outer.style.cssText = "visibility:hidden;overflow:scroll;position:absolute;width:100px;height:100px;";
      document.body.appendChild(outer);

      const inner = document.createElement("div");
      inner.style.width = "100%";
      outer.appendChild(inner);

      // Batch layout reads
      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
      const windowWidth = window.innerWidth;
      const documentWidth = document.documentElement.clientWidth;

      // Clean up
      document.body.removeChild(outer);

      // Calculate final width with fallback for mobile
      const finalWidth = scrollbarWidth > 0 ? scrollbarWidth : Math.max(0, windowWidth - documentWidth);

      // Batch style writes
      document.documentElement.style.setProperty("--scrollbar-width", `${finalWidth}px`);
      // document.documentElement.style.setProperty("--has-scrollbar", finalWidth > 0 ? "1" : "0");
    });
  }

  // Defer until page is fully loaded to avoid blocking render
  if (document.readyState === "complete") {
    setScrollbarWidth();
  } else {
    window.addEventListener("load", setScrollbarWidth, { once: true });
  }

  // Recalculate on resize/orientation change
  let resizeTimeout;
  const debouncedSetScrollbarWidth = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(setScrollbarWidth, 100);
  };

window.addEventListener("resize", debouncedSetScrollbarWidth);
window.addEventListener("orientationchange", debouncedSetScrollbarWidth);
})();

(() => {
  const PRODUCT_HANDLE = "akk-pro-gut-balance-capsules";
  const sectionSelector = ".akk-final-purchase";

  const formatMoney = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)} SGD`;

  const planBottleCount = (plan) => {
    const dataCount = Number(plan.dataset.bottleCount || 0);
    if (dataCount > 0) return dataCount;

    const text = plan.textContent.toLowerCase();
    if (text.includes("3 bottle") || text.includes("complete")) return 3;
    if (text.includes("2 bottle") || text.includes("routine")) return 2;
    return 1;
  };

  const findVariant = (variants, plan) => {
    const configuredVariantId = String(plan.dataset.variantId || "");
    const configuredVariant = variants.find((variant) => String(variant.id) === configuredVariantId);
    if (configuredVariant) return configuredVariant;

    const bottleCount = planBottleCount(plan);
    return variants.find((variant) => {
      const title = String(variant.title || "").toLowerCase();
      return title.includes(`${bottleCount} bottle`);
    });
  };

  const syncActivePlan = (section) => {
    const activePlan = section.querySelector(".akk-final-purchase__plan.is-active");
    const variantInput = section.querySelector("[data-final-variant-input]");
    const quantityInput = section.querySelector("[data-final-quantity-input]");
    if (!activePlan) return;
    if (variantInput) variantInput.value = activePlan.dataset.variantId || variantInput.value;
    if (quantityInput) quantityInput.value = activePlan.dataset.quantity || "1";
  };

  const ensureCompareNode = (priceWrap, currentNode) => {
    let compareNode = priceWrap.querySelector(".akk-final-purchase__compare");
    if (!compareNode) {
      compareNode = document.createElement("span");
      compareNode.className = "akk-final-purchase__compare";
      priceWrap.insertBefore(compareNode, currentNode);
    }
    return compareNode;
  };

  const updatePlan = (plan, variant) => {
    if (!variant) return;

    const bottleCount = planBottleCount(plan);
    const priceWrap = plan.querySelector(".akk-final-purchase__price-wrap") || plan;
    const currentNode =
      plan.querySelector(".akk-final-purchase__price-current") ||
      plan.querySelector(".akk-final-purchase__price");

    plan.dataset.variantId = String(variant.id);
    plan.dataset.quantity = "1";
    plan.dataset.available = variant.available ? "true" : "false";

    if (currentNode) currentNode.textContent = formatMoney(variant.price);

    const compareAt = Number(variant.compare_at_price || 0);
    const price = Number(variant.price || 0);
    const compareNode = ensureCompareNode(priceWrap, currentNode);
    if (compareAt > price) {
      compareNode.hidden = false;
      compareNode.textContent = formatMoney(compareAt);
    } else {
      compareNode.hidden = true;
      compareNode.textContent = "";
    }

    const perBottleNode = plan.querySelector(".akk-final-purchase__per-bottle");
    if (perBottleNode) {
      const lang = document.documentElement.lang || "";
      const isZh = lang.toLowerCase().startsWith("zh") || window.location.pathname.startsWith("/zh");
      perBottleNode.textContent = `${formatMoney(price / bottleCount)} ${isZh ? "/ \u74f6" : "/ bottle"}`;
    }
  };

  const syncAkkFinalPurchase = async () => {
    const section = document.querySelector(sectionSelector);
    if (!section || section.dataset.variantSync === "done") return;
    const plans = Array.from(section.querySelectorAll(".akk-final-purchase__plan"));
    if (!plans.length) return;

    try {
      const response = await fetch(`/products/${PRODUCT_HANDLE}.js`, { credentials: "same-origin" });
      if (!response.ok) return;
      const product = await response.json();
      const variants = Array.isArray(product.variants) ? product.variants : [];

      plans.forEach((plan) => updatePlan(plan, findVariant(variants, plan)));
      syncActivePlan(section);
      section.dataset.variantSync = "done";

      section.addEventListener("click", () => window.requestAnimationFrame(() => syncActivePlan(section)));
    } catch (error) {
      console.warn("AKK final purchase price sync failed", error);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", syncAkkFinalPurchase, { once: true });
  } else {
    syncAkkFinalPurchase();
  }
})();
